<template>
  <div class="webex-connection-status">
    <el-tooltip
      placement="bottom"
      effect="dark"
      :disabled="!showTooltip"
    >
      <div slot="content" v-html="tooltipContent"></div>
      <div class="status-indicator" @click="manualRefresh">
        <i
          :class="statusIcon"
          :style="{ color: statusColor }"
          class="status-icon"
        ></i>
        <span class="status-text">{{ statusText }}</span>
        <i
          v-if="loading"
          class="el-icon-loading refresh-icon"
        ></i>
        <i
          v-else
          class="el-icon-refresh refresh-icon"
          @click.stop="manualRefresh"
        ></i>
      </div>
    </el-tooltip>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: "WebexConnectionStatus",
  
  data() {
    return {
      connectionStatus: null,
      loading: false,
      lastRefresh: 0,
      refreshInterval: null,
      DEBOUNCE_TIME: 5000, // 5 seconds
      POLLING_INTERVAL: 30000, // 30 seconds
    };
  },

  computed: {
    isWebexEnabled() {
      return _.get(this.$store, "state.modules.webex.enabled", false);
    },

    statusColor() {
      if (!this.connectionStatus) return "#909399";
      switch (this.connectionStatus.overall) {
        case "healthy":
          return "#67c23a";
        case "warning":
          return "#e6a23c";
        case "error":
          return "#f56c6c";
        default:
          return "#909399";
      }
    },

    statusIcon() {
      if (!this.connectionStatus) return "el-icon-question";
      switch (this.connectionStatus.overall) {
        case "healthy":
          return "el-icon-success";
        case "warning":
          return "el-icon-warning";
        case "error":
          return "el-icon-error";
        default:
          return "el-icon-question";
      }
    },

    statusText() {
      if (!this.connectionStatus) return "Unknown";
      switch (this.connectionStatus.overall) {
        case "healthy":
          return "Webex Ready";
        case "warning":
          return "Webex Warning";
        case "error":
          return "Webex Sync Issues";
        default:
          return "Unknown";
      }
    },

    showTooltip() {
      return this.connectionStatus !== null;
    },

    tooltipContent() {
      if (!this.connectionStatus) return "Loading Webex status...";

      let content = "<div style='text-align: left;'>";
      
      // Notification section
      content += "<div style='margin-bottom: 10px;'>";
      content += "<strong>Notifications:</strong> ";
      content += this.connectionStatus.notificationReady.status
        ? "✅ Ready"
        : "❌ Issues";
      
      if (this.connectionStatus.notificationReady.missing.length > 0) {
        content += "<ul style='margin: 5px 0; padding-left: 20px;'>";
        this.connectionStatus.notificationReady.missing.forEach((item) => {
          content += `<li>${item}</li>`;
        });
        content += "</ul>";
      }
      content += "</div>";

      // Status updates section
      content += "<div style='margin-bottom: 10px;'>";
      content += "<strong>Status Updates:</strong> ";
      content += this.connectionStatus.statusUpdatesReady.status
        ? "✅ Ready"
        : "❌ Issues";
      
      if (this.connectionStatus.statusUpdatesReady.missing.length > 0) {
        content += "<ul style='margin: 5px 0; padding-left: 20px;'>";
        this.connectionStatus.statusUpdatesReady.missing.forEach((item) => {
          content += `<li>${item}</li>`;
        });
        content += "</ul>";
      }
      content += "</div>";

      // Instructions
      if (this.connectionStatus.overall !== "healthy") {
        content += "<div style='margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee;'>";
        content += "<strong>To fix these issues:</strong><br>";
        content += "1. Log out and back into Webex<br>";
        content += "2. Log out and back into KeyReply dashboard<br>";
        content += "3. Set your status to Offline, then Online again<br>";
        content += "4. Refresh this status<br><br>";
        content += "<strong>Important:</strong><br>";
        content += "- Use the same email account for both Webex and KeyReply<br>";
        content += "- Match environments: UAT Webex Team → UAT KeyReply, Prod Webex Team → Prod KeyReply)";
        content += "</div>";
      }

      // Last checked time
      if (this.connectionStatus.lastChecked) {
        const lastChecked = new Date(this.connectionStatus.lastChecked);
        const dateString = lastChecked.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric'
        });
        const timeString = lastChecked.toLocaleTimeString('en-US', { 
          hour12: true, 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit',
          fractionalSecondDigits: 3
        });
        content += "<div style='margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee; font-size: 11px; color: #999;'>";
        content += `<em>Last checked: ${dateString} at ${timeString}</em><br>`;
        content += `<em>Auto-refreshes every ${this.POLLING_INTERVAL / 1000} seconds</em><br>`;
        content += `<em>If you don't receive notifications or status updates, please refresh to check sync</em>`;
        content += "</div>";
      }

      content += "</div>";
      return content;
    },
  },

  async mounted() {
    if (this.isWebexEnabled) {
      await this.fetchStatus();
      this.startPolling();
    }
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    async fetchStatus() {
      try {
        this.loading = true;
        const response = await this.$rest("get", "webex_connection_status");
        // $rest returns data directly, not wrapped in response.data
        this.connectionStatus = response;
        this.lastRefresh = Date.now();
      } catch (error) {
        console.error("Failed to fetch Webex connection status:", error);
        this.$message.error("Failed to fetch Webex connection status");
      } finally {
        this.loading = false;
      }
    },

    async manualRefresh() {
      const now = Date.now();
      if (now - this.lastRefresh < this.DEBOUNCE_TIME) {
        return;
      }

      await this.fetchStatus();
    },

    startPolling() {
      this.refreshInterval = window.setInterval(() => {
        this.fetchStatus();
      }, this.POLLING_INTERVAL);
    },

    stopPolling() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
  },
};
</script>

<style scoped>
.webex-connection-status {
  display: inline-block;
  margin-left: 10px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  background-color: #f5f7fa;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-indicator:hover {
  background-color: #e6e8eb;
}

.status-icon {
  font-size: 16px;
}

.status-text {
  font-size: 12px;
  color: #606266;
}

.refresh-icon {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-icon:hover {
  color: #409eff;
  transform: rotate(180deg);
}

.el-icon-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>